'use client';

import type React from 'react';
import { createContext, type ReactNode, useContext } from 'react';
import type { LlmEvaluationData, LlmTraceData } from '../hooks/mock-llm-data';

import { useLlmData } from '../hooks/use-llm-data';

// Context type definition
type LlmDataContextType = {
  traces: LlmTraceData[];
  evaluations: LlmEvaluationData[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

// Create the context
const LlmDataContext = createContext<LlmDataContextType | undefined>(undefined);

// Provider component props
type LlmDataProviderProps = {
  children: ReactNode;
};

// Provider component
export const LlmDataProvider: React.FC<LlmDataProviderProps> = ({ children }) => {
  const llmData = useLlmData();

  return <LlmDataContext.Provider value={llmData}>{children}</LlmDataContext.Provider>;
};

// Custom hook to use the context
export const useLlmDataContext = (): LlmDataContextType => {
  const context = useContext(LlmDataContext);

  if (context === undefined) {
    throw new Error('useLlmDataContext must be used within a LlmDataProvider');
  }

  return context;
};

// Loading component to show while data is fetching
export const LlmDataLoader: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { loading, error } = useLlmDataContext();

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-64'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900'></div>
        <span className='ml-2 text-gray-600'>Loading LLM data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
        <div className='flex'>
          <div className='flex-shrink-0'>
            <svg
              className='h-5 w-5 text-red-400'
              viewBox='0 0 20 20'
              fill='currentColor'
              aria-label='Error icon'
            >
              <title>Error</title>
              <path
                fillRule='evenodd'
                d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                clipRule='evenodd'
              />
            </svg>
          </div>
          <div className='ml-3'>
            <h3 className='text-sm font-medium text-red-800'>Failed to load LLM data</h3>
            <div className='mt-2 text-sm text-red-700'>
              <p>{error.message}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
